import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

const API = 'https://api.ctexpresskh.com/api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', password: '', role: 'staff' });
  const token = localStorage.getItem('token');
  const me = JSON.parse(localStorage.getItem('user') || '{}');

  const load = () => {
    fetch(API + '/users', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => setUsers(d.data || []));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', phone: '', password: '', role: 'staff' });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, phone: u.phone, password: '', role: u.role });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error('Name and phone required'); return; }
    if (!editing && !form.password) { toast.error('Password required'); return; }
    try {
      const url = editing ? API + '/users/' + editing.id : API + '/users';
      const method = editing ? 'PUT' : 'POST';
      const body = { ...form };
      if (editing && !body.password) delete body.password;
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) { toast.success(editing ? 'User updated!' : 'User created!'); setShowModal(false); load(); }
      else toast.error(data.message);
    } catch { toast.error('Server error'); }
  };

  const toggle = async (u) => {
    if (u.id === me.id) { toast.error('Cannot deactivate yourself'); return; }
    try {
      await fetch(API + '/users/' + u.id + '/toggle', { method: 'PUT', headers: { Authorization: 'Bearer ' + token } });
      toast.success('Status updated!'); load();
    } catch { toast.error('Error'); }
  };

  const roleColor = { admin: '#7c3aed', staff: '#0891b2', customer: '#059669' };
  const roleBg = { admin: '#ede9fe', staff: '#cffafe', customer: '#d1fae5' };

  const inp = (label, key, placeholder='', type='text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1e293b' }}>Users</h1>
        <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}>
          <Plus size={18} /> New User
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              {['Name', 'Phone', 'Role', 'Status', 'Created', 'Action'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: u.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '12px 16px', fontWeight: 600 }}>{u.name} {u.id === me.id && <span style={{ fontSize: 11, color: '#f97316' }}>(you)</span>}</td>
                <td style={{ padding: '12px 16px' }}>{u.phone}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: roleBg[u.role], color: roleColor[u.role], padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{u.role}</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: u.is_active ? '#d1fae5' : '#fee2e2', color: u.is_active ? '#059669' : '#dc2626', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 12 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(u)} style={{ background: '#dbeafe', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Edit2 size={13} /> Edit
                    </button>
                    <button onClick={() => toggle(u)} style={{ background: u.is_active ? '#fee2e2' : '#d1fae5', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: u.is_active ? '#dc2626' : '#059669', fontSize: 12, fontWeight: 600 }}>
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>{editing ? 'Edit User' : 'New User'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            {inp('Full Name *', 'name', 'Full name')}
            {inp('Phone *', 'phone', 'Phone number')}
            {inp(editing ? 'New Password (leave blank to keep)' : 'Password *', 'password', '••••••••', 'password')}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 600, color: '#374151' }}>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' }}>Cancel</button>
              <button onClick={save} style={{ flex: 1, padding: 10, background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
